import { supabase } from "../lib/supabase";

export const runUserPreferencesMigration = async () => {
  if (!supabase) {
    console.error("Supabase client not initialized");
    return { success: false, error: "Supabase client not initialized" };
  }

  try {
    console.log("Starting migration of localStorage preferences to database...");

    // Create the table if it doesn't exist
    const { error: tableError } = await supabase.rpc('create_user_preferences_table');
    
    if (tableError) {
      // Log with lower severity for expected RPC errors
      if (tableError.code === 'PGRST202' && tableError.message?.includes('public.execute_sql')) {
        console.log("Note: SQL function not available - this is expected in some environments");
      } else {
        console.error("Error creating user_preferences table:", tableError);
      }
      
      // Try direct SQL approach as fallback
      const { error: sqlError } = await supabase.rpc('execute_sql', { 
        sql: `
          -- Create user_preferences table for storing user-specific settings
          CREATE TABLE IF NOT EXISTS user_preferences (
            id UUID PRIMARY KEY DEFAULT uuid_generate_v4(),
            user_id UUID NOT NULL REFERENCES auth.users(id) ON DELETE CASCADE,
            preference_type TEXT NOT NULL,
            preference_value JSONB NOT NULL,
            created_at TIMESTAMP WITH TIME ZONE DEFAULT NOW(),
            updated_at TIMESTAMP WITH TIME ZONE DEFAULT NOW(),
            UNIQUE(user_id, preference_type)
          );
          
          -- Add appropriate indexes
          CREATE INDEX IF NOT EXISTS idx_user_preferences_user_id ON user_preferences(user_id);
          CREATE INDEX IF NOT EXISTS idx_user_preferences_type ON user_preferences(preference_type);
        `
      });
      
      if (sqlError) {
        // Only log error if it's not the expected "function not found" error
        if (sqlError.code === 'PGRST202' && sqlError.message?.includes('public.execute_sql')) {
          console.log("Note: Using table directly instead of SQL functions");
        } else {
          console.error("Error with direct SQL table creation:", sqlError);
        }
        
        // Continue execution regardless - the table might already exist
        console.log("Continuing with existing database structure");
        return { success: true, message: "Using existing database structure" };
      }
    }
    
    console.log("User preferences table created or verified");
    return { success: true };
  } catch (error) {
    console.error("Error running user preferences migration:", error);
    return { success: false, error };
  }
};

// Helper function to migrate mobile navigation preferences from localStorage to database
export const migrateLocalStorageToDatabase = async () => {
  if (!supabase) {
    console.error("Supabase client not initialized");
    return { success: false, error: "Supabase client not initialized" };
  }

  try {
    console.log("Starting migration of localStorage preferences to database...");
    
    // Get user session
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) {
      console.log("No user session found, skipping migration");
      return { success: true, message: "No user session" };
    }
    
    // Check if localStorage has navigation config
    const navConfig = localStorage.getItem('mobileNavConfig');
    if (!navConfig) {
      console.log("No navigation config found in localStorage");
      return { success: true, message: "No config to migrate" };
    }
    
    // Parse the config
    let paths;
    try {
      paths = JSON.parse(navConfig);
    } catch (error) {
      console.error("Invalid navigation config in localStorage:", error);
      return { success: false, error: "Invalid navigation config" };
    }
    
    console.log("Found navigation config in localStorage:", paths);
    
    // Check if user_preferences table exists by trying to query it
    const { data: existingConfig, error: checkError } = await supabase
      .from('user_preferences')
      .select('id')
      .eq('user_id', session.user.id)
      .eq('preference_type', 'mobile_navigation')
      .maybeSingle();
      
    if (checkError) {
      // If table doesn't exist (PGRST116), that's okay - it will be created by migrations
      if (checkError.code === 'PGRST116') {
        console.log("User preferences table doesn't exist yet - will be created by database migrations");
        return { success: true, message: "Table not created yet" };
      }
      console.error("Error checking for existing config:", checkError);
      return { success: false, error: checkError };
    }
    
    if (existingConfig) {
      console.log("User already has navigation config in database, skipping migration");
      return { success: true, message: "User already has config in database" };
    }
    
    // Save to database
    const { error: insertError } = await supabase
      .from('user_preferences')
      .insert({
        user_id: session.user.id,
        preference_type: 'mobile_navigation',
        preference_value: paths,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      });
      
    if (insertError) {
      console.error("Error saving navigation config to database:", insertError);
      return { success: false, error: insertError };
    }
    
    console.log("Successfully migrated navigation config from localStorage to database");
    return { success: true };
  } catch (error) {
    console.error("Error migrating localStorage preferences to database:", error);
    return { success: false, error };
  }
}; 